import { Modal } from "react-bootstrap"
import { Formik } from "formik";
import i18next from "i18next";
import { RecordTypeEnum } from "src/api/models/Enums";
import { Button, Card, Col, Form, Row } from 'react-bootstrap';
import Select, { MultiValue, SingleValue } from 'react-select';
import _ from 'lodash';
import RequiredFormLabel from "../RequiredFormLabel/RequiredFormLabel";
import SpinnerButton from "../SpinnerButton/SpinnerButton";
import { CreateReportDto } from ".";
import { useState } from "react";
import VehicleCardBody from "../VehicleCardBody/VehicleCardBody";
import { useApiClient } from "src/hooks/useApiClient";
import { useErrorToast } from "src/hooks/useErrorToast";
import { emailIsValid, randomString, removeDuplicates } from "src/utils";
import { useAuth } from "src/hooks/useAuth";
import { useAccountTypes } from "src/hooks/useAccountTypes";
import toaster from "src/utils/toaster";
// @ts-ignore
import './reportForm.scss';
import ReactQuill from "react-quill";
import CreatableSelect from "react-select/creatable";
import { Markets, ReactSelectOption } from "src/types";
import { StellantisQuoteRequestDTO } from "src/api/models/StellantisDTO";
import * as Yup from 'yup';
import { isMarket } from "src/utils/markets";
import ValidationError from "../ValidationError/ValidationError";
import { isAzienda } from "../Stellantis/stellantisUtils";
import { OpportunityUploadFileDTO } from "src/api/models/Opportunities/OpportunityUploadFileDTO";
import { Storage } from "aws-amplify";


const emailTemplate = `
Spett.le Cliente,
<br>
con la presente le invio il preventivo per la soluzione di noleggio da lei richiesta.
In attesa di un suo gentile riscontro resto a disposizione per ogni dubbio.
<br>
Cordiali saluti
<br>
  `;

const defaultMailData = (cc: MultiValue<ReactSelectOption> = []) => ({
  to: null,
  body: emailTemplate,
  cc,
})

const validationSchema = Yup.object().shape({
  RecordTypeId: Yup.string().required(i18next.t('required_field')),
  RecordType: Yup.string().required(i18next.t('required_field')),
  FirstName: Yup.string().when('RecordTypeId', {
    is: (value: RecordTypeEnum) =>
      [RecordTypeEnum.Libero_Professionista, RecordTypeEnum.Privato].includes(
        value
      ),
    then: (schema) => schema.required(i18next.t('required_field')),
  }),
  LastName: Yup.string().when('RecordTypeId', {
    is: (value: RecordTypeEnum) =>
      [RecordTypeEnum.Libero_Professionista, RecordTypeEnum.Privato].includes(
        value
      ),
    then: (schema) => schema.required(i18next.t('required_field')),
  }),
  Company: Yup.string().when('RecordTypeId', {
    is: (value: RecordTypeEnum) => value === RecordTypeEnum.Azienda,
    then: (schema) => schema.required(i18next.t('required_field')),
  }),
  Codice_Fiscale__c: Yup.string().when('RecordTypeId', {
    is: (value: RecordTypeEnum) => value === RecordTypeEnum.Privato,

    then: (schema) => {
      if (isMarket(Markets.it)) {
        return schema
          .required(i18next.t('required_field'))
          .length(
            16,
            i18next.t('codice_fiscale_format_error_16_chars_no_symbols')
          )
          .matches(
            /^[A-Z0-9]{16}$/,
            i18next.t('codice_fiscale_format_error_16_chars_no_symbols')
          );
      }
      if (isMarket(Markets.es)) {
        return schema
          .required(i18next.t('required_field'))
          .length(9, i18next.t('codice_fiscale_format_error_9_chars'));
      }
      return schema.required(i18next.t('required_field'));
    },
    otherwise: (schema) => schema.notRequired(),
  }),
  Partita_IVA__c: Yup.string().when('RecordTypeId', {
    is: (value: RecordTypeEnum) =>
      [RecordTypeEnum.Azienda, RecordTypeEnum.Libero_Professionista].includes(
        value
      ),
    then: (schema) => {
      if (isMarket(Markets.it)) {
        return schema
          .required(i18next.t('required_field'))
          .length(11, i18next.t('business_fiscal_code_length_error_11_chars'))
          .matches(
            /^\d{11}$/,
            i18next.t('business_fiscal_code_length_error_11_chars')
          );
      }
      if (isMarket(Markets.es)) {
        return schema
          .required(i18next.t('required_field'))
          .length(9, i18next.t('business_fiscal_code_length_error_9_chars'));
      }
      return schema.required(i18next.t('required_field'));
    },
    otherwise: (schema) => schema.notRequired(),
  }),
  Email__c: Yup.string()
    .email(i18next.t('email_not_valid'))
    .required(i18next.t('email_mandatory')),
  Phone: Yup.string()
    .matches(
      /^\+?\d{1,3}?[-.\s]?\(?\d{1,3}?\)?[-.\s]?\d{1,4}[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/,
      i18next.t('phone_not_valid')
    )
    .required(i18next.t('phone_mandatory')),
  Marca__c: Yup.string().required(i18next.t('required_field')),
  Modello__c: Yup.string().required(i18next.t('required_field')),

  to: Yup.object().shape({
    value: Yup.string().required(i18next.t('required_field')),
    label: Yup.string().required(i18next.t('required_field')),
  })
    .required(i18next.t('required_field'))
    .test('valid-email', i18next.t('invalid_email'), (value) => {
      return value ? emailIsValid(value.value) : false;
    }),
  cc: Yup.array().of(
    Yup.object().shape({
      value: Yup.string().test('valid-email', i18next.t('invalid_email'), (value) =>
        value ? emailIsValid(value) : true
      ),
    })
  ),
  body: Yup.string().required(i18next.t('required_field')),
})

type CreateReportFormData = Partial<CreateReportDto & {
  vehicle: any
}>

const buildInitialValues = ({ user, userProvvigioniSegnalatore, quoteRequest }): any => {
  const quote = quoteRequest.quote!;
  const { tipoCliente, nome, cognome, ragioneSociale } = quote;
  const defaultCc = user?.username ? [{ value: user.username, label: user.username }] : [];
  const provvSegnalatore = quote.provvigioni;
  const percentageDecimal = userProvvigioniSegnalatore / 100;
  const provvDealer = parseFloat((provvSegnalatore / percentageDecimal).toFixed(2))

  return ({
    Noleggiatore__c: quoteRequest.quote?.noleggiatore,
    RecordType: tipoCliente,
    RecordTypeId: {
      azienda: RecordTypeEnum.Azienda,
      libero_professionista: RecordTypeEnum.Libero_Professionista,
      privato: RecordTypeEnum.Privato,
    }[tipoCliente],
    Name: isAzienda(quoteRequest) ? ragioneSociale : `${nome} ${cognome}`,
    FirstName: nome ?? '',
    LastName: cognome ?? '',
    Partita_IVA__c: '',
    Codice_Fiscale__c: '',
    Email__c: '',
    Phone: '',
    Marca__c: quote.marca,
    Modello__c: quote.modello,
    ID_Vehicle_HMP__c: '',
    Messaggio__c: null,
    signedAttachments: [],
    vehicle: _.pick(quote, ['marca', 'modello', 'allestimento']),
    Provvigioni_Segnalatore__c: provvSegnalatore || 0.00,
    Provvigioni_passive__c: provvDealer || 0.00,
    ...defaultMailData(defaultCc)
  })
}

type Props = {
  isOpen?: boolean;
  closeModal?: (reload?: boolean) => void;
  quoteRequest: StellantisQuoteRequestDTO;
  loading?: boolean;
}

export function CreateStellantisReportModal({ loading, quoteRequest, isOpen, closeModal = _.noop }: Props) {
  const { accountTypes } = useAccountTypes();
  const { user, userProvvigioniSegnalatore } = useAuth();
  const initialValues: Partial<CreateReportFormData | any> = buildInitialValues({
    user,
    userProvvigioniSegnalatore,
    quoteRequest
  });

  const { apiClient, handleApiResponse } = useApiClient();
  const [pendingCreation, setPendingCreation] = useState(false);
  const { showError } = useErrorToast();

  const uploadHmpPdfToOpportunityBucket = async (): Promise<OpportunityUploadFileDTO> => {
    const fileName = quoteRequest.hmpPdf?.fileName!;

    const [_public, rest] = quoteRequest.hmpPdf?.key?.split('public/')!;

    const result: any = await Storage.get(rest ?? _public, {
      bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_STELLANTIS_DOCUMENTS,
      download: true
    });

    const ext = fileName.split('.').pop();
    const rawName = fileName.split('.' + ext)[0];
    const key = `opportunity_uploads/${rawName}-${randomString(10)}.${ext}`

    const blob = result.Body;
    const hmpFile = new File([blob], fileName, { type: blob.type });

    await Storage.put(key, hmpFile, {
      contentType: hmpFile.type,
      bucket: process.env.REACT_APP_AWS_STORAGE_BUCKET_SALESFORCE_IMPORT,
    });

    return ({ filename: fileName, key: `public/${key}` });
  }

  const buildPayload = async (values: CreateReportFormData | any) => {
    const attachments = [await uploadHmpPdfToOpportunityBucket()];

    let payload = {
      RecordTypeId: values.RecordTypeId || null,
      RecordType: values.RecordType || null,
      FirstName: values.FirstName || null,
      LastName: values.LastName || null,
      Company: values.Company || null,
      Partita_IVA__c: values.Partita_IVA__c || null,
      Codice_Fiscale__c: values.Codice_Fiscale__c || null,
      Email__c: values.Email__c || null,
      Phone: values.Phone || null,
      Marca__c: values.Marca__c || null,
      Modello__c: values.Modello__c || null,
      ID_Vehicle_HMP__c: values.ID_Vehicle_HMP__c || null,
      Provvigioni_Segnalatore__c: values.Provvigioni_Segnalatore__c || 0.00,
      Provvigioni_passive__c: values.Provvigioni_passive__c || 0.00,
      signedAttachments: attachments || [],
      Noleggiatore__c: values.Noleggiatore__c || null,
      Segnalatore_HMP__c: user.username,
      emailSending: {
        to: values.to?.value,
        cc: values.cc?.map(item => item.value),
        body: values?.body,
        uploadedAttachments: attachments,
      }
    }

    return payload;
  }

  const handleFormSubmit = async (values, formikHelpers) => {
    try {
      setPendingCreation(true);
      const payload: any = await buildPayload(values);
      const res = await handleApiResponse(apiClient.salesforceOpportunityController.createNewReport(payload))

      toaster.success(i18next.t('report_created_successfully'));
      if (!!res?.warning) toaster.warning(res.warning);
      closeModal?.()
    } catch (error) {
      showError(error, i18next.t('report_creation_failed'));
    } finally {
      setPendingCreation(false);
    }
  }

  if (!quoteRequest) return null;

  return (
    <Modal
      show={isOpen}
      onHide={closeModal}
      size="xl"
    >
      <Modal.Header closeButton>
        <Modal.Title className='display-6'>{i18next.t("create_opportunity")}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Formik
          initialValues={initialValues as any}
          validationSchema={validationSchema}
          onSubmit={handleFormSubmit}
          enableReinitialize={true}
          validateOnBlur
        >
          {({
            values,
            errors,
            touched,
            handleChange,
            handleSubmit,
            setFieldValue,
          }) => {
            const isPrivato = values?.RecordTypeId === RecordTypeEnum.Privato;
            const isLiberoProfessionista = values?.RecordTypeId === RecordTypeEnum.Libero_Professionista;
            const isAzienda = values?.RecordTypeId === RecordTypeEnum.Azienda;

            return (
              <Form id="report-form" onSubmit={handleSubmit}>
                <Row className="fields-grid">
                  <Form.Group className="mb-3">
                    <RequiredFormLabel
                      label={i18next.t('type')}
                      required={!values.RecordTypeId}
                    />
                    <Select
                      menuPosition="fixed"
                      options={accountTypes}
                      isDisabled
                      onChange={(selected) => {
                        setFieldValue(
                          'RecordTypeId',
                          selected ? selected.value : null
                        );
                        setFieldValue(
                          'RecordType',
                          selected ? selected.code : null
                        );
                      }}
                      value={
                        accountTypes?.find(
                          (option) => (values.RecordTypeId ?? '') === option.value
                        ) || null
                      }
                      className={`custom-select ${touched.RecordTypeId && errors.RecordTypeId
                        ? 'select-error'
                        : ''
                        }`}
                    />
                    <ValidationError {...{ errors, touched, name: 'RecordTypeId' }} />
                  </Form.Group>
                  {!isAzienda && (
                    <>
                      <Form.Group className="mb-3">
                        <RequiredFormLabel
                          label={i18next.t('first_name')}
                          required={(isPrivato || isLiberoProfessionista) && !values.FirstName}
                        />
                        <Form.Control
                          type="text"
                          name="FirstName"
                          onChange={handleChange}
                          value={values.FirstName ?? ''}
                          isInvalid={Boolean(!!errors.FirstName && touched.FirstName)}
                        />
                        <ValidationError {...{ errors, touched, name: 'FirstName' }} />
                      </Form.Group>
                      <Form.Group className="mb-3">
                        <RequiredFormLabel
                          label={i18next.t('last_name')}
                          required={(isPrivato || isLiberoProfessionista) && !values.LastName}
                        />
                        <Form.Control
                          type="text"
                          name="LastName"
                          onChange={handleChange}
                          value={values.LastName ?? ''}
                          isInvalid={Boolean(!!errors.LastName && touched.LastName)}
                        />
                        <ValidationError {...{ errors, touched, name: 'LastName' }} />
                      </Form.Group>
                    </>)}
                  {isAzienda && (
                    <Form.Group className="mb-3">
                      <RequiredFormLabel
                        label={i18next.t('company')}
                        required={isAzienda && !values.Company}
                      />
                      <Form.Control
                        type="text"
                        name="Company"
                        onChange={handleChange}
                        value={values.Company ?? ''}
                        isInvalid={Boolean(!!errors.Company && touched.Company)}
                      />
                      <ValidationError {...{ errors, touched, name: 'Company' }} />
                    </Form.Group>
                  )}
                  {isPrivato && (
                    <Form.Group className="mb-3">
                      <RequiredFormLabel
                        label={i18next.t('fiscal_code')}
                      />
                      <Form.Control
                        type="text"
                        name="Codice_Fiscale__c"
                        onChange={handleChange}
                        value={values.Codice_Fiscale__c || ''}
                        isInvalid={Boolean(
                          !!errors.Codice_Fiscale__c &&
                          touched.Codice_Fiscale__c
                        )
                        }
                      />
                      <ValidationError {...{ errors, touched, name: 'Codice_Fiscale__c' }} />
                    </Form.Group>
                  )}
                  {isLiberoProfessionista && (
                    <Form.Group className="mb-3">
                      <RequiredFormLabel
                        label={i18next.t('business_fiscal_code')}
                      />
                      <Form.Control
                        type="text"
                        name="Partita_IVA__c"
                        onChange={handleChange}
                        value={values.Partita_IVA__c || ''}
                        isInvalid={Boolean(!!errors.Partita_IVA__c && touched.Partita_IVA__c)}
                      />
                      <ValidationError {...{ errors, touched, name: 'Partita_IVA__c' }} />
                    </Form.Group>
                  )}
                  {isAzienda && (
                    <Form.Group className="mb-3">
                      <RequiredFormLabel
                        label={i18next.t('business_fiscal_code')}
                      />
                      <Form.Control
                        type="text"
                        name="Partita_IVA__c"
                        onChange={handleChange}
                        value={values.Partita_IVA__c || ''}
                        isInvalid={Boolean(!!errors.Partita_IVA__c && touched.Partita_IVA__c)}
                      />
                      <ValidationError {...{ errors, touched, name: 'Partita_IVA__c' }} />
                    </Form.Group>
                  )}
                  <Form.Group className="mb-3">
                    <RequiredFormLabel
                      label={i18next.t('email')}
                      required={!values.Email__c}
                    />
                    <Form.Control
                      type="email"
                      name="Email__c"
                      onChange={handleChange}
                      value={values.Email__c ?? ''}
                      isInvalid={Boolean(!!errors.Email__c && touched.Email__c)}
                    />
                    <ValidationError {...{ errors, touched, name: 'Email__c' }} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <RequiredFormLabel
                      label={i18next.t('mobile_phone')}
                      required={!values.Phone}
                    />
                    <Form.Control
                      type="text"
                      name="Phone"
                      onChange={handleChange}
                      value={values.Phone || ''}
                      isInvalid={Boolean(!!errors.Phone && touched.Phone)}
                    />
                    <ValidationError {...{ errors, touched, name: 'Phone' }} />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <RequiredFormLabel
                      label={i18next.t('rental_company')}
                      required={false}
                    />
                    <Form.Control
                      type="text"
                      name="Noleggiatore__c"
                      disabled
                      onChange={handleChange}
                      value={values.Noleggiatore__c || ''}
                    />
                  </Form.Group>
                  <Form.Group className="mb-3">
                    <RequiredFormLabel
                      label={i18next.t('commissions')}
                      required={false}
                    />
                    <Form.Control
                      type="text"
                      name="Provvigioni_Segnalatore__c"
                      disabled
                      value={values.Provvigioni_Segnalatore__c}
                    />
                  </Form.Group>
                </Row>
                <Row className='mb-3 mt-3'>
                  <RequiredFormLabel
                    label={i18next.t('vehicle')}
                    required={!values.Modello__c && !values.Marca__c}
                  />
                  <Card className='mt-2'>
                    <VehicleCardBody
                      data={{
                        dati_veicolo: values.vehicle,
                        image_list: []
                      }}
                    />
                  </Card>
                </Row>

                <Row>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{i18next.t('to_recipient')}</Form.Label>
                      <CreatableSelect
                        isClearable
                        components={{ DropdownIndicator: null }}
                        placeholder="A"
                        noOptionsMessage={() => i18next.t('add_customer_email')}
                        value={values.to}
                        formatCreateLabel={(i) => (emailIsValid(i) ? `Invia a ${i}` : `${i}`)}
                        onChange={(newValue: SingleValue<ReactSelectOption>) => {
                          if (!newValue || emailIsValid(newValue.value)) setFieldValue('to', newValue);
                        }}
                      />
                      <ValidationError {...{ errors, touched, name: 'to' }} />
                    </Form.Group>
                  </Col>
                  <Col md={6}>
                    <Form.Group className="mb-3">
                      <Form.Label>{i18next.t('cc_recipients')}</Form.Label>
                      <CreatableSelect
                        isMulti
                        components={{ DropdownIndicator: null }}
                        placeholder="C.c."
                        noOptionsMessage={() => i18next.t('add_cc_recipients')}
                        value={values.cc}
                        formatCreateLabel={(i) => (emailIsValid(i) ? `Aggiungi ${i}` : `${i}`)}
                        onChange={(newValue: MultiValue<ReactSelectOption>) => {
                          const filtered: ReactSelectOption[] = removeDuplicates(
                            newValue.filter((el) => emailIsValid(el.value))
                          );
                          setFieldValue('cc', filtered);
                        }}
                      />
                      <ValidationError {...{ errors, touched, name: 'cc' }} />
                    </Form.Group>
                  </Col>
                </Row>
                <Row className="mt-2">
                  <Col xs={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>{i18next.t('message')}</Form.Label>
                      <ReactQuill
                        theme="snow"
                        placeholder={i18next.t('write_email')}
                        value={values.body}
                        onChange={(body) => setFieldValue('body', body)}
                      />
                      <ValidationError {...{ errors, touched, name: 'message' }} />
                    </Form.Group>
                  </Col>
                </Row>
                <Row>
                  <Col xs={12}>
                    <Form.Group className="mb-3">
                      <Form.Label>{i18next.t('attachments')}</Form.Label>
                      <div className="fw-bold">{quoteRequest?.hmpPdf?.fileName}</div>
                    </Form.Group>
                  </Col>
                </Row>

                <Row className="mt-4">
                  <Col md={12} className="d-flex justify-content-end">
                    <Button
                      variant="primary"
                      type="submit"
                      className="me-2"
                      disabled={pendingCreation}
                    >
                      <SpinnerButton
                        busy={pendingCreation}
                        label={i18next.t('save_and_send')}
                      />
                    </Button>

                    <Button onClick={() => closeModal(false)} disabled={pendingCreation}>
                      <SpinnerButton label={i18next.t('close')} />
                    </Button>
                  </Col>
                </Row>
              </Form>
            );
          }}
        </Formik>
      </Modal.Body>
    </Modal>
  )
}
